import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { ConsentBanner, ConversionDock } from "@/components/site/Dock";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import BulkOrders from "@/pages/BulkOrders";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import Faqs from "@/pages/Faqs";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import { Privacy, Terms } from "@/pages/Legal";
import { track } from "@/lib/site";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
    return undefined;
  }, [pathname, hash]);
  useEffect(() => { track("page_view", { page_path: pathname }); }, [pathname]);
  return null;
}

function Layout() {
  return (
    <div className="flex min-h-screen flex-col bg-white text-[#161616]">
      <a href="#main" className="kl-skip-link" data-testid="skip-to-content-link">Skip to content</a>
      <Header />
      <main id="main" className="flex-1 overflow-x-clip" tabIndex={-1}>
        <Outlet />
      </main>
      <Footer />
      <ConversionDock />
      <ConsentBanner />
      <Toaster position="top-center" richColors={false} />
    </div>
  );
}

const SERVICE_SLUGS = ["printing-branding", "custom-tshirt-printing", "uniforms-embroidery", "xhosa-cultural-products", "corporate-gifts", "personalised-gifts", "banners-stickers"];

const REDIRECTS = {
  "/services": "/printing-branding",
  "/our-services": "/printing-branding",
  "/products": "/custom-tshirt-printing",
  "/t-shirt-printing": "/custom-tshirt-printing",
  "/uniforms": "/uniforms-embroidery",
  "/xhosa-products": "/xhosa-cultural-products",
  "/cultural-products": "/xhosa-cultural-products",
  "/bulk": "/bulk-orders",
  "/our-work": "/gallery",
  "/portfolio": "/gallery",
  "/about-us": "/about",
  "/faq": "/faqs",
  "/contact-us": "/contact",
  "/quote": "/contact",
  "/request-a-quote": "/contact",
  "/privacy": "/privacy-policy",
  "/terms-and-conditions": "/terms",
};

function App() {
  return (
    <BrowserRouter>
      <ScrollManager />
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          {SERVICE_SLUGS.map((s) => (<Route key={s} path={`/${s}`} element={<ServicePage />} />))}
          <Route path="/bulk-orders" element={<BulkOrders />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/about" element={<About />} />
          <Route path="/faqs" element={<Faqs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          {Object.entries(REDIRECTS).map(([from, to]) => (<Route key={from} path={from} element={<Navigate to={to} replace />} />))}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
